import {
    Directive, DestroyRef, Input, OnChanges, OnInit, SimpleChanges, inject
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { NgControl } from "@angular/forms";
import {
    MAT_DATE_FORMATS
} from "@angular/material/core";
import {
    DateTime
} from "luxon";
import {
    distinctUntilChanged
} from "rxjs";
import { MGD_DATE_FORMATS } from "../date-picker-formats";
import { DatePicker } from "../date-picker";
import { TimeOfDay } from "@shopliftr/common-js/data";

/**
 * Input directive for date picker
 */
@Directive({
    selector: "[mgdDatePicker]",
    providers: [
        {
            provide: MAT_DATE_FORMATS,
            useValue: MGD_DATE_FORMATS
        }
    ]
})
export class DatePickerDirective extends DatePicker implements OnInit, OnChanges {

    @Input() mgdTimezone: string;

    @Input() mgdTimeOfDay: TimeOfDay;

    private _dateTime: DateTime;

    private readonly _destroyRef = inject(DestroyRef);

    private readonly _control = inject(NgControl);

    ngOnInit(): void {

        this._dateTime = this._control.control?.value;

        const abstractControl = this._control.control;

        abstractControl.valueChanges.pipe(
            distinctUntilChanged((previous: DateTime, current: DateTime) => this._compareFn(previous, current)),
            takeUntilDestroyed(this._destroyRef)
        ).subscribe((newValue: DateTime) => {

            if (newValue) {
                abstractControl.markAsTouched();
            }
            if (DateTime.isDateTime(newValue) || !newValue) {
                const date = this._updateDate(newValue);
                this._dateTime = date;
                if (this._dateTime !== newValue) {
                    abstractControl.setValue(this._dateTime);
                }
            }
            else {
                this._control.control.setErrors({ notDateTime: true });
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {

        if (changes.mgdTimezone) {
            if (this._dateTime) {
                const date: DateTime = this._coerceTime(this._dateTime, this.mgdTimeOfDay, this.mgdTimezone);
                this._control.control.setValue(date, {
                    emitModelToViewChange: false
                });
            }
        }

        if (changes.mgdTimeOfDay) {
            if (this._dateTime) {
                const date: DateTime = this._coerceTime(this._dateTime, this.mgdTimeOfDay, this.mgdTimezone);
                this._control.control.setValue(date, {
                    emitModelToViewChange: false
                });
            }
        }
    }

    /**
     * Get datetime after applying timezone and timeOfDay
     */
    private _updateDate(newValue: DateTime): DateTime {

        const timezone = this.mgdTimezone ?? DateTime.local().zone;
        const timeOfDay = this.mgdTimeOfDay ?? TimeOfDay.StartOfDay;
        return this._coerceTime(newValue, timeOfDay, timezone);
    }
}
